import React from 'react';
import { graphql } from 'gatsby';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import LinkCard from './LinkCard';
import TextRenderer from '../../TextRenderer';
import { headers } from '../../../styles/type';
import { containerStyles } from '../../../styles/layout';
import { rem } from '../../../utilities/style';
import { media } from '../../../theme/media';
import { colors } from '../../../theme/colors';

const Container = styled.div`
  background-color: ${colors.blue['50']};
  padding: ${rem(40)} 0;

  ${media('>small')} {
    padding: ${rem(80)} 0;
  }

  ${({ darkMode }) => darkMode && `
    background-color: ${colors.blue['800']};
  `}
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Headline = styled.h2`
  ${headers.h3}
  text-align: center;
  margin-bottom: ${rem(40)};
  color: ${colors.blue['800']};

  ${media('>small')} {
    margin-bottom: ${rem(64)};
  }

  ${({ darkMode }) => darkMode && `
    color: ${colors.white};
  `}
`;

const Grid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(8)};

  ${media('>small')} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${media('>medium')} {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const LinkCards = ({ headline, linkCardItems }) => {
  const darkMode = useTheme().pageOverrides?.includes('Dark Mode');
  return (
    <Container darkMode={darkMode}>
      <ContentWrapper>
        {headline && (
          <Headline darkMode={darkMode}>
            <TextRenderer text={headline} />
          </Headline>
        )}
        {linkCardItems && (
          <Grid>
            {
              linkCardItems.map((cardItem, index) => (
                <LinkCard
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  headline={cardItem.headline}
                  image={cardItem.image}
                  text={cardItem.text.text}
                  buttonText={cardItem.buttonText}
                  buttonLink={cardItem.buttonLink}
                  darkMode={darkMode}
                />
              ))
            }
          </Grid>
        )}
      </ContentWrapper>
    </Container>
  );
};

LinkCards.propTypes = {
  headline: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  linkCardItems: PropTypes.arrayOf(
    PropTypes.object
  )
};

export const query = graphql`
  fragment LinkCardsFragment on ContentfulLinkCards {
    id
    __typename
    headline
    linkCardItems {
      image {
        gatsbyImageData(width: 208, height: 298, quality: 75, resizingBehavior: FILL)
      }
      headline
      text {
        text
      }
      buttonLink
      buttonText
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default LinkCards;
